import { useIsLoggedInWithLoading, useSessionTyped } from '@dreamstack/auth'
import { trpc } from '@dreamstack/trpc/client'
import type { Session } from 'next-auth'
import type { FunctionComponent } from 'react'
import { useCallback, useEffect, useState } from 'react'
import type { PropertySearchFilterValues } from '../../next/PropertySearchFilter'
import { SortByOptions } from '../../next/PropertySearchFilter'
import { useGetPropstackSavedQueryFromFilter } from '../../savedSearch/useGetPropstackSavedQueryFromFilter'

declare global {
  interface Window {
    _ubeConfig: any
    _userEmail: string
  }
}

type UnbounceMessageEvent = {
  type: string
  formData: string
}

type SearchProfilePopupData = {
  buyPrice: string
  space: string
  propertyType: string
  location: string
}

export const TriggerSearchProfilePopup: FunctionComponent<
  React.PropsWithChildren<unknown>
> = () => {
  const { loading, isLoggedIn } = useIsLoggedInWithLoading()
  const session = useSessionTyped()
  const { mutateAsync: upsertQueryMutation } = trpc.savedQueries.upsertSavedQuery.useMutation(
)
    const { data } = trpc.savedQueries.getMySavedQueries.useQuery(undefined, {
        enabled: isLoggedIn,
        trpc: { ssr: false },
        staleTime: 0,
    })
  const transformFilterToPropstackSavedQuery =
    useGetPropstackSavedQueryFromFilter()

  const [hasSavedQueries, setHasSavedQueries] = useState(false)
  // TODO: Write this in own component and add to PropertyDetail as sideEffect.
  // Please change ID to Dev Popup when testing the popup locally
  // ID for Dev popup: e0590abd-dace-4a75-b266-e348c3bb6a08
  // ID for Prod popup: b7145513-69ba-476a-bd01-397c20adf9ae
  const code = `window._ubeConfig = window._ubeConfig || {};
    window._ubeConfig.shouldShow = function(id) {
      if (id === 'b7145513-69ba-476a-bd01-397c20adf9ae') {
        if(${isLoggedIn} && ${!hasSavedQueries}) { return true; }
          else { return false; }
        return false;
      } else {
        return true;
      }
    };`

  const listenToMessage = useCallback(
    (event: MessageEvent<UnbounceMessageEvent>) => {
      const saveSearchQuery = async (
        data: SearchProfilePopupData,
        session: Session
      ) => {
        if (!!session) {
          const price_to = parseInt(data.buyPrice.split(' ')[0])
          const living_space = parseInt(data.space.split(' ')[0])
          const propertyType = getTypeOfImmo(data.propertyType)

          const untransformedFilter: PropertySearchFilterValues = {
            regions: [],
            cities: [data.location],
            locations: [],
            priceRange: [0, price_to],
            roomRange: [0, 10],
            livingspace: [living_space, 1000],
            sortBy: SortByOptions.FIRST_PUBLISHED_AT_ASC,
            sortDirection: 'desc',
            value: 'first_published_at',
            typeOfImmo: propertyType,
            rented: 'both',
            textSearch: '',
            constructionAgeType: 'both',
            rsCategories: [],
            otherFilters: [],
          }
          const transformedFilter = transformFilterToPropstackSavedQuery({
            filter: untransformedFilter,
          })

          const mutationResult = await upsertQueryMutation({
            query: transformedFilter,
          })
        }
      }
      if (!(event.data?.type === 'unbounce_add_search_query')) return
      const data = JSON.parse(event.data?.formData)
      if (
        ~event.origin.indexOf(
          'https://33d5d0c3773646d99f1e86db6e6ec395.pages.ubembed.com'
        )
      ) {
        if (!!session) {
          saveSearchQuery(data.formDataSavedQuery, session)
        }
      } else {
        return
      }
    },
    [session, transformFilterToPropstackSavedQuery, upsertQueryMutation]
  )

  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.addEventListener('message', listenToMessage)
      return () => {
        window.removeEventListener('message', listenToMessage)
      }
    }
  }, [listenToMessage])

  useEffect(() => {
    const savedQueries = data?.data || []
    setHasSavedQueries(savedQueries.length > 0)
    if (typeof window !== 'undefined') {
      const script = document.createElement('script')
      script.setAttribute('id', 'ubeConfig-script')
      script.type = 'text/javascript'
      script.innerHTML = code
      script.async = true
      const scriptElement = document.getElementById('ubeConfig-script')
      if (!!scriptElement) {
        document.body.replaceChild(script, scriptElement)
      } else document.body.appendChild(script)
    }
  }, [data?.data, session, isLoggedIn, code])

  return null
}

const getTypeOfImmo = (type: string) => {
  switch (type) {
    case 'Anlageobjekt':
      return 'INVESTMENT'
    case 'Wohnung':
      return 'APARTMENT'
    case 'Büro/Praxis':
      return 'OFFICE'
    case 'Einzelhandel':
      return 'STORE'
    default:
      return 'all'
  }
}
